/*
 * Weld propagation for direct manipulation (DOM-free, unit-tested).
 *
 * When a corner moves, every wall endpoint welded to it moves too. Unlocked
 * walls simply have that endpoint dragged along (they stretch); LOCKED walls
 * keep their length, so they translate rigidly — both endpoints join the move
 * and the propagation continues from their far corner (BFS).
 */
import type { Doc, Pt, Wall } from './types.ts';
import { WELD_TOL, wallVec } from './geom.ts';
import { clamp } from './utils.ts';
import { restoreWalls, snapshotWalls, solveConstraints } from './solver.ts';

export interface WeldMove { wall: Wall; end: 0 | 1; x: number; y: number }

/**
 * Endpoints that must translate with a moving corner.
 * rigidFirst: treat walls at the starting corner(s) as rigid regardless of lock
 * (used when typing a length — the adjoining wall gets pushed, not sheared).
 */
export function collectWeld(walls: Wall[], startPts: Pt[], excludeIds: Set<string>, rigidFirst: boolean): WeldMove[] {
  const key = (p: Pt) => Math.round(p.x * 16) + ',' + Math.round(p.y * 16);
  const seen = new Set(startPts.map(key));
  const handled = new Set<string>();
  const moves: WeldMove[] = [];
  const queue = startPts.map(p => ({ x: p.x, y: p.y, depth: 0 }));
  while (queue.length) {
    const c = queue.pop()!;
    for (const w of walls) {
      if (excludeIds.has(w.id)) continue;
      for (const end of [0, 1] as const) {
        const ex = end ? w.x2 : w.x1, ey = end ? w.y2 : w.y1;
        if (Math.hypot(ex - c.x, ey - c.y) >= WELD_TOL) continue;
        const hk = w.id + ':' + end;
        if (handled.has(hk)) continue;
        handled.add(hk);
        moves.push({ wall: w, end, x: ex, y: ey });
        if (w.locked || (rigidFirst && c.depth === 0)) {
          const ox = end ? w.x1 : w.x2, oy = end ? w.y1 : w.y2;
          const hk2 = w.id + ':' + (1 - end);
          if (!handled.has(hk2)) { handled.add(hk2); moves.push({ wall: w, end: (1 - end) as 0 | 1, x: ox, y: oy }); }
          const ok = key({ x: ox, y: oy });
          if (!seen.has(ok)) { seen.add(ok); queue.push({ x: ox, y: oy, depth: c.depth + 1 }); }
        }
      }
    }
  }
  return moves;
}

export const applyWeld = (moves: WeldMove[], dx: number, dy: number): void => {
  for (const m of moves) {
    if (m.end) { m.wall.x2 = m.x + dx; m.wall.y2 = m.y + dy; }
    else { m.wall.x1 = m.x + dx; m.wall.y1 = m.y + dy; }
  }
};

/**
 * Lengthen/shorten a wall by moving its far end: walls joined at that corner
 * translate with it (pushing the room), welded corners beyond stretch to stay
 * attached, and locked walls anywhere in the chain translate instead of shearing.
 * Returns false — with everything reverted — when the new length contradicts
 * the wall's constraints (Fusion rejects the edit rather than compromising).
 */
export function shiftWallEnd(doc: Doc, wl: Wall, dx: number, dy: number): boolean {
  const snap = snapshotWalls(doc);
  const moves = collectWeld(doc.walls, [{ x: wl.x2, y: wl.y2 }], new Set([wl.id]), true);
  wl.x2 += dx; wl.y2 += dy;
  applyWeld(moves, dx, dy);
  if (!solveConstraints(doc, new Set([wl.id]), [wl.id]).ok) {
    restoreWalls(snap);
    return false;
  }
  return true;
}

/**
 * One frame of a Fusion-style endpoint drag: put the dragged corner at `np`,
 * translate welded endpoints along, then solve. Stage 1 holds the whole
 * dragged wall; if its own constraints make that impossible (axis/angle with
 * an off-axis mouse), stage 2 holds only the far corner and lets the
 * constraints steer the dragged end to the nearest legal spot. If even that
 * fails, the frame is rejected and geometry stays where it was.
 * `weld`/`start` let the caller pass drag-start data; omitted, they're
 * collected from the current corner position.
 */
export function dragWallEnd(doc: Doc, wl: Wall, end: 0 | 1, np: Pt,
                            weld?: WeldMove[], start?: Pt): boolean {
  const cur = end ? { x: wl.x2, y: wl.y2 } : { x: wl.x1, y: wl.y1 };
  const s = start || cur;
  const moves = weld || collectWeld(doc.walls, [cur], new Set([wl.id]), false);
  const fixed = end ? { x: wl.x1, y: wl.y1 } : { x: wl.x2, y: wl.y2 };
  // The dragged wall's own hard constraints shape the target BEFORE anything
  // is staged: an axis pins the direction, a length lock pins the radius.
  // Staging an off-manifold position would smear the weld pre-pass (a locked
  // neighbor translated by a bogus delta), which the solver then takes out on
  // far walls — the "dragging a corner moved the opposite side" bug.
  const ax = (doc.constraints || []).find(c => c.type === 'axis' && c.a === wl.id);
  if (ax?.type === 'axis') np = ax.axis === 'h' ? { x: np.x, y: fixed.y } : { x: fixed.x, y: np.y };
  if (wl.locked) {
    const L = Math.hypot(wl.x2 - wl.x1, wl.y2 - wl.y1);
    const dx = np.x - fixed.x, dy = np.y - fixed.y, dl = Math.hypot(dx, dy);
    np = dl > 1e-9 ? { x: fixed.x + dx / dl * L, y: fixed.y + dy / dl * L } : { ...cur };
  }
  const snap = snapshotWalls(doc);
  if (end) { wl.x2 = np.x; wl.y2 = np.y; } else { wl.x1 = np.x; wl.y1 = np.y; }
  applyWeld(moves, np.x - s.x, np.y - s.y);
  if (!solveConstraints(doc, new Set([wl.id]), [wl.id]).ok &&
      !solveConstraints(doc, new Set(), [wl.id], { pinPts: [fixed] }).ok) {
    restoreWalls(snap);
    return false;
  }
  return true;
}

/** Keep every opening within its (possibly shortened) wall. */
export function clampOpenings(doc: Doc): void {
  for (const o of doc.openings) {
    const wl = doc.walls.find(w => w.id === o.wallId); if (!wl) continue;
    const len = wallVec(wl).len;
    o.t = clamp(o.t, Math.min(o.width / 2, len / 2), Math.max(o.width / 2, len - o.width / 2));
  }
}
